import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IGoogleMapsMarker } from "@app/interfaces/IGoogleMapsMarkerOptions";
import { ContextualSpeedingOverride } from "@app/main/components/resourceManagement/contextual-speeding-settings-manager/models/contextual-speeding-override";
import { HereRouteData } from "@app/main/components/resourceManagement/contextual-speeding-settings-manager/models/here-route-data";
import { IPolyline } from "@typings/google.maps.angular";
import { lastValueFrom } from "rxjs";
import { GeocodeService } from "./geocode.service";
import { GoogleMarkerService } from "./googleMarker.service";
import { TranslationService } from "./translation.service";

@Injectable({
    providedIn: 'root'
})
export class ContextualSpeedingOverrideService {
    constructor(
        private readonly httpClient: HttpClient,
        private readonly geocodeService: GeocodeService,
        private readonly translationService: TranslationService,
        private readonly googleMarkerService: GoogleMarkerService
    ) { }

    async getContextualSpeedingOverrides(): Promise<ContextualSpeedingOverride[]> {
        return await lastValueFrom(this.httpClient.get<ContextualSpeedingOverride[]>(`/api/speeding/override`));
    }

    async getPointsForContextualSpeedingOverride(overrideId: number): Promise<[number, number][]> {
        return await lastValueFrom(this.httpClient.get<[number, number][]>(`/api/speeding/override/${overrideId}/points`));
    }

    async getContextualSpeedingOverrideRoute(points: [number, number][]): Promise<HereRouteData> {
        return await lastValueFrom(this.httpClient.post<HereRouteData>(`/api/speeding/override/route`, points));
    }

    async addContextualSpeedingOverride(override: ContextualSpeedingOverride): Promise<ContextualSpeedingOverride> {
        return await lastValueFrom(this.httpClient.post<ContextualSpeedingOverride>(`/api/speeding/override`, override));
    }

    async updateContextualSpeedingOverride(override: ContextualSpeedingOverride): Promise<any> {
        return await lastValueFrom(this.httpClient.post<ContextualSpeedingOverride>(`/api/speeding/override/${override.Id}`, override));
    }

    async deleteContextualSpeedingOverride(thresholdId: number): Promise<any> {
        return await lastValueFrom(this.httpClient.delete(`/api/speeding/override/${thresholdId}`));
    }

    async getAddressForMarker(marker: IGoogleMapsMarker<any>): Promise<string> {
        if (!marker?.options?.position?.lat || !marker?.options?.position?.lng) {
            return;
        }

        const latLngs = new google.maps.LatLng({
            lat: marker?.options?.position?.lat,
            lng: marker?.options?.position?.lng
        });

        const response = await this.geocodeService.getAddressFromPoint(latLngs);

        if (!response?.LocationDescription) {
            return;
        }

        return response.LocationDescription;
    }

    createPolyline(points: google.maps.LatLng[]): IPolyline {
        return {
            id: points.length,
            path: points,
            options: {
                stroke: {
                    color: "#000000",
                    weight: 4,
                    opacity: 1
                }
            }
        };
    }

    createMapMarker(point: [number, number], type: 'start' | 'end'): IGoogleMapsMarker<string> {
        const translationKey = type === 'start'
            ? 'ContextualSpeedingRoadEditor.Start'
            : 'ContextualSpeedingRoadEditor.End'

        return this.googleMarkerService.createLabeledMarker({
            label: this.translationService.t(translationKey),
            id: type === 'start' ? 1 : 2,
            lat: point[0],
            lng: point[1],
            icon: type === 'start' ? "play_arrow" : "stop",
        });
    }
}
